import type {
  PartnerStatus,
  PartnerSummary,
} from "@redotech/admin-sdk/rpc/schema/partners/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback, useState } from "react";

import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { mapPartnerErrorToFieldError } from "../lib/map-partner-error-to-field-errors";

interface PartnerActionPanelProps {
  partner: PartnerSummary;
  /** Called after any action that mutates the partner so the detail page refetches. */
  onPartnerChanged: () => void;
}

type PendingAction = "pause" | "reactivate" | "terminate" | null;

export const PartnerActionPanel = memo(function PartnerActionPanel({
  partner,
  onPartnerChanged,
}: PartnerActionPanelProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingAction>(null);
  const [submitting, setSubmitting] = useState(false);

  const isTerminated = partner.status === "terminated";
  const isPaused = partner.status === "paused";
  const isActive = partner.status === "active";

  const handleResend = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await rpcClient.resendMagicLink({ partnerId: partner.id });
      if (result.magicLinkSent) {
        toast("Magic link sent.", { variant: "success" });
      } else {
        toast("Magic link delivery failed. Try again later.", {
          variant: "warning",
        });
      }
      onPartnerChanged();
    } catch (err) {
      const mapping = mapPartnerErrorToFieldError(err);
      toast(
        mapping?.message ??
          (err instanceof Error ? err.message : "Failed to resend magic link."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [rpcClient, partner.id, onPartnerChanged]);

  const handleStatusChange = useCallback(
    async (status: PartnerStatus) => {
      setSubmitting(true);
      try {
        await rpcClient.updatePartner({ partnerId: partner.id, status });
        toast(
          status === "active"
            ? "Partner reactivated."
            : status === "paused"
              ? "Partner paused."
              : "Partner terminated.",
          { variant: "success" },
        );
        onPartnerChanged();
      } catch (err) {
        const mapping = mapPartnerErrorToFieldError(err);
        toast(
          mapping?.message ??
            (err instanceof Error
              ? err.message
              : "Failed to update partner status."),
          { variant: "error" },
        );
      } finally {
        setSubmitting(false);
        setPendingConfirmation(null);
      }
    },
    [rpcClient, partner.id, onPartnerChanged],
  );

  return (
    <Flex align="center" dir="row" gap="sm">
      <RedoButton
        disabled={isTerminated || submitting}
        hierarchy="secondary"
        onClick={() => void handleResend()}
        size="sm"
        text="Resend magic link"
      />
      {isActive && (
        <RedoButton
          disabled={submitting}
          hierarchy="secondary"
          onClick={() => setPendingConfirmation("pause")}
          size="sm"
          text="Pause"
        />
      )}
      {isPaused && (
        <RedoButton
          disabled={submitting}
          hierarchy="secondary"
          onClick={() => setPendingConfirmation("reactivate")}
          size="sm"
          text="Reactivate"
        />
      )}
      <RedoButton
        disabled={isTerminated || submitting}
        hierarchy="secondary"
        onClick={() => setPendingConfirmation("terminate")}
        size="sm"
        text="Terminate"
      />

      {pendingConfirmation === "pause" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Pausing…" : "Pause partner",
            onClick: () => void handleStatusChange("paused"),
            pending: submitting,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="The partner will lose login access until reactivated. Pausing is reversible."
          title="Pause partner?"
        />
      )}

      {pendingConfirmation === "reactivate" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Reactivating…" : "Reactivate partner",
            onClick: () => void handleStatusChange("active"),
            pending: submitting,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="The partner will regain login access immediately."
          title="Reactivate partner?"
        />
      )}

      {pendingConfirmation === "terminate" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Terminating…" : "Terminate partner",
            onClick: () => void handleStatusChange("terminated"),
            pending: submitting,
            theme: "destructive",
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="Termination is permanent. Existing partner sessions are revoked on the next request and the partner can't be reactivated through this UI."
          theme="warn"
          title="Terminate partner?"
        />
      )}
    </Flex>
  );
});
